import { useCallback, useEffect, useReducer, useState, type FormEvent, type ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { account, AccountError, type AccountDescription } from '../auth/account'
import { config } from '../config'
import { enrolment } from '../model/enrolment'
import { Loading } from '../ui/QueryState'
import { QrCode } from '../ui/QrCode'

// The signed-in reader's own account. docs/plan/account.md §5.
//
// Four cards, top to bottom, each one form with one submit and no state
// outliving its request. Everything here is the account port's; nothing
// on this screen knows which pool operation a button is, or what the
// pool calls a refusal — the port has already turned that into the
// sentence drawn under the form.

/**
 * The account screen.
 *
 * **Under `none` it is one sentence.** The local loop has no identity:
 * no token is sent and every reader is an operator, so there is nothing
 * to describe, no password and no factor (§2.7).
 */
export function Account() {
  if (config.authMode === 'none') {
    return <Typography>The credential-free loop has no account to manage.</Typography>
  }
  return <SignedInAccount />
}

function SignedInAccount() {
  const [described, setDescribed] = useState<AccountDescription | null>(null)
  const [failed, setFailed] = useState<unknown>(null)

  const describe = useCallback(() => {
    account.describe().then(
      (next) => {
        setDescribed(next)
        setFailed(null)
      },
      setFailed,
    )
  }, [])
  useEffect(describe, [describe])

  if (failed && !described) return <Refusal error={failed} />
  if (!described) return <Loading label="Reading your account…" />

  return (
    <Stack spacing={4} sx={{ maxWidth: 720 }}>
      <Stack spacing={1}>
        <Typography variant="h1">Account</Typography>
        <Typography variant="body2" color="text.secondary">
          Who you are signed in as, and how you sign in. These are yours, not any site&rsquo;s.
        </Typography>
      </Stack>
      <WhoYouAre described={described} onSaved={describe} />
      <Password />
      <TwoStep mfa={described.mfa} onChanged={describe} />
      <Sessions />
    </Stack>
  )
}

/** A refusal from the port, in its own words. */
function Refusal({ error }: { error: unknown }) {
  const title = error instanceof AccountError ? error.title : 'Something went wrong'
  const message = error instanceof Error ? error.message : String(error)
  return (
    <Alert severity="error">
      {title && <AlertTitle>{title}</AlertTitle>}
      {message}
    </Alert>
  )
}

function Section({ title, caption, children }: { title: string; caption: string; children: ReactNode }) {
  return (
    <Stack spacing={1.5} component="section" aria-label={title}>
      <Stack spacing={0.5}>
        <Typography variant="h2">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      </Stack>
      <Paper sx={{ p: 2.5 }}>{children}</Paper>
    </Stack>
  )
}

/**
 * One request's lifecycle: busy while it runs, its refusal after. Every
 * card holds one of these per form and nothing longer-lived.
 */
function useRequest() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const run = useCallback(async (request: () => Promise<void>): Promise<boolean> => {
    setBusy(true)
    setError(null)
    try {
      await request()
      return true
    } catch (refused) {
      setError(refused)
      return false
    } finally {
      setBusy(false)
    }
  }, [])
  return { busy, error, run, clear: () => setError(null) }
}

// ---------------------------------------------------------------------
// 5.1 Who you are
// ---------------------------------------------------------------------

function WhoYouAre({ described, onSaved }: { described: AccountDescription; onSaved: () => void }) {
  const [name, setName] = useState(described.name ?? '')
  // What the pool holds, as far as this form knows: the read it was drawn
  // from, then whatever it last saved. A blur straight after Enter is the
  // same value and sends nothing.
  const [held, setHeld] = useState(described.name ?? '')
  const [saved, setSaved] = useState(false)
  const request = useRequest()

  const save = async (event?: FormEvent) => {
    event?.preventDefault()
    const next = name.trim()
    if (next === held || request.busy) return
    setSaved(false)
    if (await request.run(() => account.updateName(next))) {
      setHeld(next)
      setSaved(true)
      onSaved()
    }
  }

  return (
    <Section title="Who you are" caption="The address you sign in with, and what to call you.">
      <Stack spacing={2.5} component="form" onSubmit={save}>
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Email
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ overflowWrap: 'anywhere' }}>{described.email}</Typography>
            <Chip
              size="small"
              variant="outlined"
              color={described.emailVerified ? 'success' : 'warning'}
              label={described.emailVerified ? 'verified' : 'unverified'}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            To sign in with a different address, ask whoever invited you to invite that one.
          </Typography>
        </Stack>
        <TextField
          label="Name"
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            setSaved(false)
          }}
          onBlur={() => void save()}
          helperText="Optional. Shown above your email in the sidebar."
          autoComplete="name"
          disabled={request.busy}
        />
        {saved && <Alert severity="success">Saved.</Alert>}
        {Boolean(request.error) && <Refusal error={request.error} />}
      </Stack>
    </Section>
  )
}

// ---------------------------------------------------------------------
// 5.2 Password
// ---------------------------------------------------------------------

function Password() {
  const [current, setCurrent] = useState('')
  const [proposed, setProposed] = useState('')
  const [again, setAgain] = useState('')
  const [changed, setChanged] = useState(false)
  const request = useRequest()
  const mismatched = again !== '' && again !== proposed

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setChanged(false)
    if (await request.run(() => account.changePassword(current, proposed))) {
      setCurrent('')
      setProposed('')
      setAgain('')
      setChanged(true)
    }
  }

  return (
    <Section title="Password" caption="Change it here if you know the current one. If you have forgotten it, sign out and use “Forgot your password?” on the sign-in page.">
      <Stack spacing={2} component="form" onSubmit={submit}>
        <TextField
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={proposed}
          onChange={(event) => setProposed(event.target.value)}
          helperText="At least 12 characters, with an upper-case letter, a lower-case letter, a number and a symbol."
        />
        <TextField
          label="New password again"
          type="password"
          autoComplete="new-password"
          value={again}
          onChange={(event) => setAgain(event.target.value)}
          error={mismatched}
          helperText={mismatched ? 'The two new passwords are not the same.' : ' '}
        />
        {changed && (
          <Alert severity="success">
            Password changed. Other devices stay signed in — use <em>Sign out everywhere</em> below if that is not what
            you want.
          </Alert>
        )}
        {Boolean(request.error) && <Refusal error={request.error} />}
        <Box>
          <Button
            type="submit"
            variant="contained"
            disabled={request.busy || !current || !proposed || proposed !== again}
          >
            Change password
          </Button>
        </Box>
      </Stack>
    </Section>
  )
}

// ---------------------------------------------------------------------
// 5.3 Two-step verification
// ---------------------------------------------------------------------

function TwoStep({ mfa, onChanged }: { mfa: 'off' | 'totp'; onChanged: () => void }) {
  const [state, dispatch] = useReducer(enrolment, { step: mfa === 'totp' ? 'on' : 'off' })
  useEffect(() => dispatch({ type: 'described', mfa }), [mfa])
  const [code, setCode] = useState('')
  const [confirmingOff, setConfirmingOff] = useState(false)
  const request = useRequest()

  const begin = () =>
    request.run(async () => {
      const begun = await account.beginTotp()
      setCode('')
      dispatch({ type: 'begun', ...begun })
    })

  // Through the one request like every other form, so a second press
  // while the first is in flight sends nothing. The refusal is drawn
  // under the code by the reducer rather than as the request's alert,
  // because that is where the reader is looking.
  const confirm = async (event: FormEvent) => {
    event.preventDefault()
    if (request.busy) return
    await request.run(async () => {
      try {
        await account.confirmTotp(code.trim())
      } catch (refused) {
        dispatch({ type: 'refused', message: refused instanceof Error ? refused.message : String(refused) })
        return
      }
      dispatch({ type: 'confirmed' })
      onChanged()
    })
  }

  const turnOff = async () => {
    if (await request.run(() => account.disableTotp())) {
      setConfirmingOff(false)
      dispatch({ type: 'disabled' })
      onChanged()
    }
  }

  return (
    <Section
      title="Two-step verification"
      caption="A code from an app on your phone, asked for after your password. Someone who learns your password still cannot sign in without your phone."
    >
      {state.step === 'off' && (
        <Stack spacing={2} alignItems="flex-start">
          <Typography>
            Two-step verification is off. Turning it on takes an authenticator app — Google Authenticator, Microsoft
            Authenticator, 1Password or similar.
          </Typography>
          {Boolean(request.error) && <Refusal error={request.error} />}
          <Button variant="contained" onClick={() => void begin()} disabled={request.busy}>
            Turn on
          </Button>
        </Stack>
      )}

      {state.step === 'enrolling' && (
        <Stack spacing={2.5} component="form" onSubmit={confirm}>
          <Typography>Scan this with your authenticator app.</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
            <QrCode value={state.otpauthUri} label="A QR code for your authenticator app" />
            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="text.secondary">
                Or enter this key by hand:
              </Typography>
              <Typography
                component="code"
                sx={{ fontFamily: 'monospace', fontSize: 15, overflowWrap: 'anywhere', userSelect: 'all' }}
              >
                {state.secret}
              </Typography>
            </Stack>
          </Stack>
          <TextField
            label="Enter the code your app shows now."
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
            error={Boolean(state.error)}
            helperText={state.error ?? ' '}
            sx={{ maxWidth: 280 }}
          />
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" disabled={code.length !== 6 || request.busy}>
              Confirm
            </Button>
            <Button onClick={() => dispatch({ type: 'cancelled' })}>Cancel</Button>
          </Stack>
        </Stack>
      )}

      {state.step === 'on' && (
        <Stack spacing={2} alignItems="flex-start">
          <Typography>
            Two-step verification is on. You will be asked for a code from your app when you sign in.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            New phone? Turn it off, then on again, and scan the new code.
          </Typography>
          <Button color="error" variant="outlined" onClick={() => setConfirmingOff(true)}>
            Turn off…
          </Button>
        </Stack>
      )}

      <Dialog
        open={confirmingOff}
        onClose={() => {
          setConfirmingOff(false)
          request.clear()
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Turn off two-step verification?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              Signing in will ask for your password only. The code your app shows stops working; to turn it on again
              you will scan a new one.
            </DialogContentText>
            {Boolean(request.error) && <Refusal error={request.error} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingOff(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={request.busy} onClick={() => void turnOff()}>
            Turn off
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  )
}

// ---------------------------------------------------------------------
// 5.4 Sessions
// ---------------------------------------------------------------------

function Sessions() {
  const [confirming, setConfirming] = useState(false)
  const request = useRequest()

  return (
    <Section title="Sessions" caption="Every browser you are signed in on.">
      <Stack spacing={2} alignItems="flex-start">
        <Typography>
          Signed in on other devices? <em>Sign out everywhere</em> ends every session, including this one.
        </Typography>
        <Button color="error" variant="outlined" onClick={() => setConfirming(true)}>
          Sign out everywhere…
        </Button>
      </Stack>
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sign out everywhere?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText>
              Every browser signed in to your account, this one included, is signed out. Each will need your password
              {' '}— and your code, if two-step verification is on — to sign in again.
            </DialogContentText>
            {Boolean(request.error) && <Refusal error={request.error} />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={request.busy}
            // On success the port has already sent the reader to the
            // pool's logout, so there is nothing left here to close.
            onClick={() => void request.run(() => account.signOutEverywhere())}
          >
            Sign out everywhere
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  )
}
